import React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { Link, type LinkProps, useLocation } from "@tanstack/react-router";
import { cn } from "~/lib/cn";
import { Check } from "lucide-react";

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
      className,
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

// Legacy components for backward compatibility
export const TabList = ({
  className,
  children,
  ...props
}: {
  className?: string;
  children?: React.ReactNode;
}) => {
  return (
    <nav
      className={cn(
        "text-muted-foreground border-b-1 inline-flex h-10 items-center justify-start gap-2 border-b-gray-200",
        className,
      )}
      {...props}
    >
      {children}
    </nav>
  );
};

interface TabProps {
  to?: LinkProps["to"];
  href?: string;
  staticLink?: boolean;
  completed?: boolean;
  children?: React.ReactNode;
  className?: string;
}

export const Tab = ({
  completed,
  children,
  className,
  to,
  href,
  staticLink,
}: TabProps) => {
  const location = useLocation();
  const baseClasses =
    "ring-offset-background focus-visible:ring-ring border-b-1 hover:border-b-brand-600 hover:text-brand-600 -mb-2 inline-flex items-center justify-center whitespace-nowrap px-4 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 relative gap-2";
  const activeClasses = "border-b-brand-700 text-brand-700";

  const content = (
    <>
      {children}
      {completed !== undefined && (
        <div
          className={cn(
            "flex h-4 w-4 items-center justify-center rounded-full text-xs text-white",
            completed ? "bg-lime-500" : "bg-gray-200",
          )}
        >
          {completed && <Check className="size-[1em]" />}
        </div>
      )}
    </>
  );

  if (staticLink && href) {
    // For static links, check if current path matches href
    // For offer pages, check if path starts with the href (to handle /offer/123 matching /offer)
    const isActive = href.endsWith("/offer")
      ? location.pathname.startsWith(href)
      : location.pathname === href;
    return (
      <a
        href={href}
        className={cn(baseClasses, isActive && activeClasses, className)}
        data-state={isActive ? "selected" : ""}
      >
        {content}
      </a>
    );
  }

  return (
    <Link
      to={to!}
      className={cn(baseClasses, className)}
      activeProps={{
        className: cn(baseClasses, activeClasses, className),
      }}
    >
      {content}
    </Link>
  );
};

export const TabContent = ({
  children,
  ...props
}: {
  children?: React.ReactNode;
}) => {
  return (
    <div
      className="ring-offset-background focus-visible:ring-ring mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      {...props}
    >
      {children}
    </div>
  );
};

export { Tabs, TabsList, TabsTrigger, TabsContent };
